import type { ReactNode } from "react";

import type { ItemProps } from "@citrine/atoms/item";
import type { RenderProps } from "@citrine/atoms/render";
import { Render } from "@citrine/atoms/render";
import type { ColorName } from "@citrine/theme";
import type { DataFullstoryId } from "@citrine/types/full-story";

import type * as sx from "./tabs.sx";

export type TabLabelProps<Value extends string = string> = {
  color?: ColorName;
  error?: boolean;
  errorTooltip?: ReactNode;
  icon?: ReactNode;
  progress?: boolean;
  size?: sx.Size;
  success?: boolean;
  warning?: boolean;
  warningTooltip?: ReactNode;
} & ItemProps<Value>;

/** Enables usage of `${dataFullstoryId}-content` for TabPanels */
type TabDataFullstoryId<T extends string> = T extends DataFullstoryId
  ? `${T}-content` extends DataFullstoryId
    ? T
    : never
  : never;

export type Tab<Value extends string = string> = {
  "data-testid"?: string;
  "data-fullstoryid"?: TabDataFullstoryId<DataFullstoryId>;
  preserve?: boolean;
} & RenderProps &
  TabLabelProps<Value>;

interface TabPanelProps {
  tab: Tab;
  active?: boolean;
  className?: string;
}

const noDisplayStyle = { display: "none" };

export function TabPanel({ tab, active, className }: TabPanelProps) {
  const { "data-fullstoryid": dataFullstoryid, ...tabProps } = tab;
  return (
    <div
      data-fullstoryid={
        active && dataFullstoryid ? `${dataFullstoryid}-content` : undefined
      }
      className={className}
      role="tabpanel"
      hidden={!active}
      // use style instead of class for highest precedence
      style={active ? undefined : noDisplayStyle}
      id={`tabpanel-${tab.value}`}
      aria-labelledby={`tab-${tab.value}`}
    >
      {active || tab.preserve ? <Render {...tabProps} /> : null}
    </div>
  );
}
